<script setup lang="ts">
import {
  createDefaultColorOptions,
  createMarkupEditorToolbar,
  createMarkupEditorToolStyles,
  getEditorDefaults
} from '@pqina/pintura'
import localeDeDE, {
  MarkupEditor as localeMarkupEditorDeDE
} from '@pqina/pintura/locale/de_DE/core/de_DE.js'
import localeAnnotateDeDE from '@pqina/pintura/locale/de_DE/annotate/de_DE.js'
import localeCropDeDE from '@pqina/pintura/locale/de_DE/crop/de_DE.js'
import localeFilterDeDE from '@pqina/pintura/locale/de_DE/filter/de_DE.js'
import localeFinetuneDeDE from '@pqina/pintura/locale/de_DE/finetune/de_DE.js'
import localeResizeDeDE from '@pqina/pintura/locale/de_DE/resize/de_DE.js'
import { enabled } from 'virtual:nuxt-pwa-configuration'
import 'moment/dist/locale/de'

const { siteName, appVersion } = useAppConfig()
useHead({
  titleTemplate: (titleChunk) => {
    return titleChunk ? `${titleChunk} - ${siteName}` : siteName
  },
  meta: [
    { name: 'version', content: `${appVersion}` },
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
    }
  ]
})

const router = useRouter()
const { $pwa } = useNuxtApp()
const { t, setLocale } = useL10n()
const { locale } = useI18n()
setLocale('de')
locale.value = 'de' as any
const route = useRoute()
const toast = useToast()
const user = useUserStore()
const languageStore = useLanguageStore()
const customFieldStore = useCustomFieldStore()
const notifications = useNotifications()

const { navigateToCurrentReport, navigateToLastVisitedPage, currentReport } = useCurrentReport()

const showNotificationDialog = ref(false)
const appReady = ref(false)

const tabItems = computed(() => {
  const baseItems = [
    {
      id: 0,
      route: '/dashboard',
      icon: 'pi pi-home'
    }
  ]

  if (currentReport.value) {
    baseItems.push({
      id: 1,
      route: 'current-report',
      icon: 'pi pi-briefcase'
    })
  }

  if (currentReport.value?.lastVisitedPage) {
    baseItems.push({
      id: 2,
      route: 'go-to-latest-edited-current-report-page',
      icon: 'pi pi-file'
    })
  }

  baseItems.push({
    id: 3,
    route: '/camera',
    icon: 'pi pi-camera'
  })

  return baseItems
})
const cameraActive = ref(false)
provide('cameraActive', cameraActive)

const showFooterNav = computed(() => {
  const base = route.fullPath.split('?')[0]
  if (base === '/login') return false
  if (base === '/camera') {
    return !cameraActive.value
  }
  return true
})
const activeTab = computed<number | null>(() => {
  const base = route.fullPath.split('?')[0]

  // Check if we're on a project-data page (current report)
  if (base.startsWith('/project-data/') && currentReport.value) {
    const reportTabIndex = tabItems.value.findIndex((tab) => tab.route === 'current-report')
    return reportTabIndex >= 0 ? reportTabIndex : null
  }

  const idx = tabItems.value.findIndex((tab) => base === tab.route.split('?')[0])
  return idx >= 0 ? idx : null
})

const handleTabClick = (tab: any) => {
  if (tab.route === 'current-report') {
    navigateToCurrentReport()
  } else if (tab.route === 'go-to-latest-edited-current-report-page') {
    navigateToLastVisitedPage()
  } else {
    navigateTo(tab.route)
  }
}

onMounted(async () => {
  // Keep loading screen visible for minimum time
  const mountStartTime = Date.now()
  const minimumLoadingTime = 1500 // 1.5 seconds minimum

  // Remove the SPA loading template immediately when Vue is ready
  const splash = document.getElementById('inspect-splash')
  if (splash) {
    console.log('[App] Removing SPA splash screen')
    splash.remove()
  }

  provideGlobalState()

  if (enabled && 'serviceWorker' in navigator) {
    try {
      await navigator.serviceWorker.ready
    } catch (error) {
      toast.add({ severity: 'warn', detail: 'Offline features may not work' })
    }
  }

  window.addEventListener('chatMessageReceived', (event: any) => {
    const { summary, detail } = event.detail
    toast.add({
      severity: 'info',
      summary,
      detail,
      life: 3000
    })
  })

  if (!user.isLoggedIn) {
    navigateTo('/login')
  }

  if (user.isLoggedIn) {
    const mandantSettings = await useApi('tenant/settings', 'GET')
    customFieldStore.setData(mandantSettings?.data ?? {})

    const hasSuperVisor = await useApi(`users/${user.user.id}`, 'GET')?.then(
      (res) => !!res?.data?.supervisor
    )
    user.setHasSupervisor(hasSuperVisor)
  }

  if (user.isLoggedIn) {
    useTenantSettingsStore().fetchTenantSettings()
    // Fetch and cache project custom field options for offline use
    customFieldStore.fetchProjectCustomFieldOptions()
  }

  menuItems.value = [
    {
      label: t('global.userMenu.reload'),
      icon: 'pi pi-refresh',
      command: () => {
        router.go(0)
        $pwa?.updateServiceWorker(true)
      }
    },
    {
      label: t('global.userMenu.settings'),
      icon: 'pi pi-cog',
      command: () => {
        showNotificationDialog.value = true
      }
    },
    {
      label: t('global.userMenu.logout', { username: user?.user?.name }),
      icon: 'pi pi-sign-out',
      command: async () => {
        if (notifications.isSubscribed.value) {
          try {
            await notifications.unsubscribe()
          } catch {}
        }

        user.calendarCategories = []
        user.setHasSupervisor(null)

        await user.logout()
        navigateTo('/login')
      }
    }
  ]

  imageEditorProps.value = getEditorDefaults()

  if (languageStore.language === 'german') {
    imageEditorProps.value.locale = {
      ...localeDeDE,
      ...localeMarkupEditorDeDE,
      ...localeAnnotateDeDE,
      ...localeCropDeDE,
      ...localeFilterDeDE,
      ...localeFinetuneDeDE,
      ...localeResizeDeDE
    }
  }

  imageEditorProps.value.enableZoomControls = false

  imageEditorProps.value.utils = ['annotate', 'crop', 'filter', 'finetune', 'resize']

  imageEditorProps.value.markupEditorToolbar = createMarkupEditorToolbar([
    'sharpie',
    'ellipse',
    'arrow',
    'rectangle',
    'line',
    'text',
    'preset'
  ])

  imageEditorProps.value.markupEditorToolStyles = createMarkupEditorToolStyles({
    sharpie: {
      backgroundColor: createDefaultColorOptions().transparent,
      strokeColor: createDefaultColorOptions().red,
      strokeWidth: '1.75%'
    },
    marker: {
      backgroundColor: createDefaultColorOptions().transparent,
      strokeColor: createDefaultColorOptions().red,
      strokeWidth: 12
    },
    ellipse: {
      backgroundColor: createDefaultColorOptions().transparent,
      strokeColor: createDefaultColorOptions().red,
      strokeWidth: 12
    },
    rectangle: {
      backgroundColor: createDefaultColorOptions().transparent,
      strokeColor: createDefaultColorOptions().red,
      strokeWidth: 12
    },
    arrow: {
      strokeWidth: 12
    },
    text: {
      fontSize: 48
    }
  })

  // Calculate remaining time to show loading screen
  const elapsedTime = Date.now() - mountStartTime
  const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime)

  // Hide loading screen after minimum time
  setTimeout(() => {
    console.log('[App] Ready - hiding loading screen')
    appReady.value = true
  }, remainingTime)
})
</script>
<template>
  <div>
    <!-- Loading Screen - shown by default until appReady -->
    <Transition name="fade-loading">
      <div v-if="!appReady" class="inspect-loading-screen">
        <div class="logo-container">
          <img src="/logo.svg" alt="Inspect Logo" />
        </div>

        <div class="loading-content">
          <p class="loading-subtitle">Anwendung wird geladen...</p>

          <div class="progress-bar-container">
            <div class="progress-bar"></div>
          </div>

          <div class="loading-dots">
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </div>
        </div>
      </div>
    </Transition>

    <NuxtLayout :key="languageStore.language">
      <VitePwaManifest />
      <NuxtPage />
      <Toast />
      <!-- <UpdateNotification /> -->

      <Dialog
        v-model:visible="showNotificationDialog"
        modal
        :header="$t('components.settingsPanel.title')"
        :style="{ width: '90vw', maxWidth: '600px' }"
        class="notification-dialog"
      >
        <SettingsPanel />
      </Dialog>

      <div
        v-if="isCalendarEventLoading"
        class="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
      >
        <div class="bg-white rounded-lg p-8 max-w-sm mx-4 text-center shadow-2xl">
          <div class="mb-4">
            <i class="pi pi-spin pi-spinner text-4xl text-blue-600"></i>
          </div>
          <h3 class="text-xl font-semibold mb-2 text-gray-800">
            {{ $t('pages.project-data.loadingReport') }}
          </h3>
          <p class="text-gray-600">
            {{ $t('pages.project-data.pleaseWaitWhileLoading') }}
          </p>
          <div class="mt-4">
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div class="bg-blue-600 h-2 rounded-full animate-pulse" style="width: 100%"></div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showFooterNav" class="relative mt-14 z-10 pt-3">
        <Card
          class="!p-0 fixed bottom-0 left-0"
          :pt="{ root: { class: 'w-full' }, body: { class: '!p-0 !w-full' } }"
        >
          <template #content>
            <div class="tabs-container">
              <Tabs :value="activeTab!">
                <TabList>
                  <Tab
                    v-for="tab in tabItems"
                    :key="tab.id"
                    :value="tab.id"
                    class="flex gap-2"
                    @click="handleTabClick(tab)"
                  >
                    <i :class="tab.icon" />
                  </Tab>
                </TabList>
              </Tabs>
            </div>
          </template>
        </Card>
      </div>
    </NuxtLayout>
  </div>
</template>
<style lang="scss">
body {
  background-color: var(--surface-500);
  padding-left: 20px;
  padding-right: 20px;
}
div {
  line-height: normal;
}
.p-card {
  background-color: white !important;
  border-radius: 0.25rem !important;
  padding: 0.75rem !important;
}
.p-card-body {
  padding: 4px !important;
}
.p-card-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 0;
  padding-bottom: 0;
}
.p-component {
  font-size: 0.875rem;
}
.p-inputtext::placeholder,
.p-textarea::placeholder,
.p-placeholder {
  color: var(--p-surface-400) !important;
  font-size: 16px;
}
.p-multiselect .p-multiselect-label.p-placeholder {
  color: var(--p-surface-400) !important;
  font-size: 16px;
}
.error {
  color: crimson;
  font-weight: 500;
}
.p-select {
  z-index: 1 !important;
}
.p-select-option-label {
  white-space: pre-line;
}
.p-toast {
  position: fixed;
  top: 0;
  right: 0;
  left: 20px;
  z-index: 1000;
  width: 80% !important;
}

.fieldTitle {
  color: var(--primary-color);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.buttonHeader {
  background-color: var(--primary-500) !important;
  border: 0 !important;
  padding: 0.5rem 1.8rem !important;
}
.p-button-label {
  color: white !important;
}
.p-menu-item-label,
.p-menu-item-icon {
  color: var(--text-color) !important;
}
.p-iftalabel {
  input {
    border-radius: 16px;
    padding: 1rem;
  }
  label {
    margin-top: 0.3rem;
    margin-left: 0.4rem;
  }
}
.groupTitle {
  font-size: 16px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: black;
}
.selectInput {
  width: 100%;
  height: 3.5rem;
  align-items: center;

  .p-datepicker-input,
  .p-inputnumber-input,
  button {
    height: 100%;
  }
}
.checkboxGroup {
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.PinturaToolbar {
  .PinturaButton {
    background-color: #8e9291 !important;
  }
}
.PinturaButtonIcon {
  color: white;
}
.PinturaButtonLabel {
  color: white !important;
}
.PinturaRoot .PinturaButtonExport {
  background-color: #8e9291 !important;
  padding: 0.4rem 1.25rem !important;

  .PinturaButtonInner {
    color: white !important;
  }
}

.PinturaButtonIconOnly {
  background-color: #8e9291 !important;
  border-color: white !important;
  padding: 0.4rem 1.25rem !important;

  .PinturaButtonInner {
    color: white !important;
  }
}
.PinturaButton[disabled] {
  background-color: #8e9291 !important;
  filter: none !important;

  .PinturaButtonInner {
    color: #8e9291 !important;
  }

  svg {
    opacity: 0.75 !important;
  }
}
.PinturaDropdownButton,
.pintura-editor-panel {
  background-color: #8e9291 !important;
}
.submitButton {
  width: 100% !important;
  padding: 1.25rem !important;
  border-radius: 30px !important;
}
.signaturePad {
  border-style: solid;
  border-width: 1px;
  border-color: #d1d5db;
  background-color: white;
  border-radius: 16px;
  width: 100% !important;
}
.p-multiselect {
  background: #ffffff;
  height: min-content;

  .p-multiselect-label {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    color: #495057 !important;
  }
}

.p-multiselect-filter {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  font-size: 0.875rem;
}

.p-multiselect-dropdown,
.p-select-dropdown {
  width: unset;
  min-width: 3rem;
  padding-right: 0.5rem;
  pointer-events: auto;
}
.changeInputTypeButton {
  border: none !important;
  background-color: transparent !important;
  padding: 0 !important;

  .p-button-label {
    color: var(--primary-color) !important;
  }
  &:hover {
    background-color: transparent !important;
    border: none !important;
  }
}
.todaysAppointments,
.todaysReports {
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1rem;
  color: #4b5563 !important;
}
.tabs-container {
  width: 100%;
  overflow: hidden;
}
.tabs-container .p-tab {
  flex: 1;
  height: 68px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #ffffff;
  border-width: 3px 0 0 0;

  span {
    color: #64748b;
    font-weight: 600;
  }

  i {
    font-size: 1.5rem;
  }
}
.p-tab-active span {
  color: var(--primary-color) !important;
}
.p-select {
  outline: none;
}
.p-select:focus {
  outline: none;
}
.p-multiselect {
  outline: none;
}
.p-multiselect:focus {
  outline: none;
}
.p-textarea,
.p-inputtext {
  font-size: 16px !important;
}

/* Loading Screen Styles */
.inspect-loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f7f7fa;
  z-index: 999999;
}

.fade-loading-enter-active,
.fade-loading-leave-active {
  transition: opacity 0.5s ease;
}

.fade-loading-enter-from,
.fade-loading-leave-to {
  opacity: 0;
}

.logo-container {
  margin-bottom: 3rem;
  animation: fadeInScale 0.6s ease-out;
}

.logo-container img {
  width: 200px;
  height: auto;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
}

.loading-content {
  width: 90%;
  max-width: 500px;
  text-align: center;
}

.loading-title {
  color: #45494e;
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 1rem;
  animation: fadeIn 0.8s ease-out 0.2s both;
}

.loading-subtitle {
  color: #6b7280;
  font-size: 1rem;
  margin-bottom: 2rem;
  animation: fadeIn 1s ease-out 0.4s both;
}

.progress-bar-container {
  width: 100%;
  height: 8px;
  background-color: #e5e7eb;
  border-radius: 9999px;
  overflow: hidden;
  animation: fadeIn 1.2s ease-out 0.6s both;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #45494e 0%, #6b7280 50%, #45494e 100%);
  background-size: 200% 100%;
  border-radius: 9999px;
  animation: progressAnimation 2s ease-in-out infinite;
  width: 0%;
}

.loading-dots {
  display: inline-flex;
  gap: 0.25rem;
  margin-top: 2rem;
  animation: fadeIn 1.4s ease-out 0.8s both;
}

.dot {
  width: 12px;
  height: 12px;
  background-color: #45494e;
  border-radius: 50%;
  animation: dotPulse 1.4s ease-in-out infinite;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes progressAnimation {
  0% {
    width: 0%;
    background-position: 0% 50%;
  }
  50% {
    width: 70%;
    background-position: 100% 50%;
  }
  100% {
    width: 100%;
    background-position: 200% 50%;
  }
}

@keyframes dotPulse {
  0%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}
</style>
